/**
 * Tax Calculate V2 API service.
 *
 * Promise-based wrapper around `api/v1/TaxCalculateV2` (TaxCalculateV2Controller).
 * Same axios / base-URL / cookie-auth pattern as `services/taxConfigV2Api.js`:
 * a dedicated instance with `withCredentials`, and an `unwrap()` that treats the
 * ResponseModel `{ message, status, data }` envelope's `status === 0`
 * (APIStatus.Successful) as success and throws an Error carrying `.status`/`.data`
 * otherwise. Callers `await` the inner payload directly.
 *
 * The V2 hierarchy mirrors V1's — Tax Config version → CALC VERSION → formula —
 * except a "formula" here is a **C# function** (a source file with an entry point),
 * not a Blockly graph. So the three levels are:
 *
 *   TaxConfigVersionV2  →  TaxCalcVersionV2  →  TaxCalcFunctionV2
 *   (/TaxConfigurationV2)  (/TaxCalculateV2/:configVersionId)
 *                                              (…/:calcVersionId/function/:functionId)
 *
 * Semantics the UI must respect (they are why several of these methods exist at all):
 *
 *  • **Calc versions are single-active per config version.** `setVersionActive(id, true)`
 *    deactivates every sibling server-side; the caller should mirror that locally rather
 *    than assume only one row changed.
 *  • **`cloneVersion` copies the functions too, and the clone is ALWAYS inactive** —
 *    activating it is a separate, deliberate act (CloneTaxCalcVersionV2Request has no
 *    `isActive` field to send).
 *  • **`deleteVersion` cascades to that version's functions.** Confirm with the count first.
 *  • **A save never fails because the code does not compile.** `createFunction` /
 *    `updateFunction` resolve normally and report the outcome on the returned row
 *    (`compileStatus` + `compileErrors`). Treat a compile error as a saved-with-errors
 *    row, never as a failed request.
 *  • **`validateFunction` and `runFunction` return HTTP 200 with `ok: false`** for a
 *    compile / binding / timeout / runtime failure — they RESOLVE, they do not throw.
 *    Only a real transport or envelope failure rejects. Read `ok`, `errorKind`,
 *    `errorMessage`, `errorParameter` and `errors[]` off the resolved value.
 *  • **`functionCode` IS the generated C# class name** (`^[A-Za-z_][A-Za-z0-9_]*$`).
 *    Renaming it server-side rewrites the class declaration in the stored source —
 *    but ONLY when the request does not also carry `sourceCode`. If the editor changed
 *    both, send both and let the compiler have the last word.
 *  • **`parameterBindings` is the ONE field of the save body that is not a blind full replace**, because
 *    partial saves exist here (the list page's ▲▼ reorder sends a row without its bindings, and under a
 *    full-replace rule sorting two formulas would delete both formulas' wiring):
 *      – omitted / `null` → **keep** what is stored
 *      – `[]`             → **clear** every binding
 *      – non-empty array  → replace the whole set
 *    An editor that RENDERS the binding table should still send the whole list on every save, INCLUDING
 *    entries whose parameter has disappeared from the current signature — the backend never auto-deletes
 *    a binding, so leaving an orphan out of a non-empty list is the one way to drop it by accident.
 *  • `isStale` on a summary/full row means the cached `compileStatus` describes an OLDER
 *    source than the one now stored — render it as "needs recompiling", never as a green tick.
 *
 * NOTE (JS project — no TypeScript): shapes are documented via the JSDoc typedefs below.
 * JSON is camelCase with the ID suffix preserved (`calcVersionID`, `functionID`) exactly
 * like `taxQuestionV2Store.js`'s `questionVersionID`.
 *
 * @typedef {Object} CalcVersion
 * @property {number} calcVersionID @property {number} taxConfigVersionID
 * @property {string} versionCode @property {?string} description @property {boolean} isActive
 * @property {number} functionCount
 * @property {?string} createdBy @property {?string} createdDate
 * @property {?string} updatedBy @property {?string} updatedDate
 *
 * @typedef {Object} FunctionParameter
 * @property {string} name @property {string} type
 * @property {boolean} required  `false` = the parameter has a default the run can fall back on
 * @property {*} default
 *
 * @typedef {Object} ParameterBinding
 * Where ONE parameter of the `Run(...)` method gets its value from when the formula is executed
 * against a tax form — the admin-authored counterpart of `FunctionParameter` (which only describes
 * what the C# signature declares). A parameter with NO entry falls back to the default written in
 * the source, which is why the editor omits blank rows instead of sending `value: ''`.
 * @property {string} name  parameter name, matched to `FunctionParameter.name` verbatim (C# is case-sensitive)
 * @property {'FIELD'|'CONSTANT'} source
 * @property {?string} fieldKey  `source: 'FIELD'` only — a SOFT reference to `TaxConfigRuleV2.fieldKey`;
 *   it is NOT a foreign key, so a key that no longer exists in the registry stays stored (and must stay
 *   on screen) rather than being cleared.
 * @property {?string} value  `source: 'CONSTANT'` only — kept as a string; the backend binder converts it
 *   to the parameter's C# type.
 *
 * @typedef {Object} FunctionIssue
 * @property {string} message @property {?number} line @property {?number} column @property {?string} id
 *
 * @typedef {Object} FunctionSummary
 * @property {number} functionID @property {number} calcVersionID
 * @property {string} functionCode @property {?string} name @property {?string} description
 * @property {?string} fieldKey @property {?string} returnType
 * @property {FunctionParameter[]} parameters
 * @property {?ParameterBinding[]} parameterBindings  the INPUT bindings (`fieldKey` above is the OUTPUT one)
 * @property {'Draft'|'Compiled'|'Error'} compileStatus @property {number} compileErrorCount
 * @property {boolean} isStale @property {?string} compiledDate
 * @property {number} sortOrder @property {boolean} isActive
 * @property {?string} updatedBy @property {?string} updatedDate
 *
 * @typedef {FunctionSummary & {
 *   sourceCode: string, sourceHash: ?string, compileErrors: FunctionIssue[],
 *   bindingWarnings: ?FunctionIssue[], createdBy: ?string, createdDate: ?string
 * }} FunctionFull
 * `bindingWarnings` reads the bindings against the signature the SAVE just compiled — an unbound
 * required parameter, a binding whose parameter is gone, an unknown FieldKey, a constant that cannot
 * become the parameter's type. It is never a reason to treat the save as failed, and it is kept off
 * `compileErrors`/`compileErrorCount` so it cannot turn the compile badge red. Only create/update
 * populate it; `getFunction` answers with it absent.
 *
 * @typedef {Object} ValidateResult
 * @property {boolean} ok @property {'Draft'|'Compiled'|'Error'} compileStatus
 * @property {?string} entryPoint @property {?string} returnType
 * @property {FunctionParameter[]} parameters @property {FunctionIssue[]} errors
 *
 * @typedef {Object} RunResult
 * @property {boolean} ok @property {string} functionCode @property {?string} entryPoint
 * @property {boolean} compiled  `false` = served from the compiled-assembly cache
 * @property {number} elapsedMs @property {*} result
 * @property {?('compile'|'binding'|'timeout'|'runtime')} errorKind
 * @property {?string} errorMessage @property {?string} errorDetail @property {?string} errorParameter
 * @property {FunctionIssue[]} errors
 * @property {string[]} warnings  Thai notes from resolving the bindings against `formId` (empty without one)
 * @property {string[]} missingParameters  required parameters neither the arguments nor the bindings filled
 *
 * @typedef {Object} CalcSnapshot
 * One row of the «ประวัติการบันทึก» table = ONE write of the whole formula set of a calc version.
 * Every create / update / delete of a formula takes one (deduped when the content did not change),
 * so the history is a per-version timeline, not a per-formula one.
 * @property {number} snapshotID @property {number} calcVersionID
 * @property {number} snapshotNo  the `v{N}` chip — 1-based, counted per calc version
 * @property {'SAVE'|'BASELINE'|'RESTORE'|'IMPORT'} sourceAction
 * @property {?number} restoredFromNo  the `snapshotNo` a RESTORE came from (null on every other action)
 * @property {?string} comment @property {number} functionCount
 * @property {?string} createdBy
 * @property {string} createdDate  🔴 no `Z` on the wire — render through `utils/thaiTime.js`
 * @property {boolean} isCurrent  highest `snapshotNo` = the version as it stands now (disable restore there)
 * @property {?string} payloadJson  the formula rows as a RAW JSON STRING (`TaxCalcFunctionV2[]`) — present only
 *   on `getSnapshot`, always null in the list. This service does NOT parse it: hand the string to
 *   `utils/taxCalcSnapshotDiff.js` → `diffSnapshots(leftPayloadJson, rightPayloadJson)`.
 *
 * @typedef {Object} RestoreCalcSnapshotResult
 * @property {number} calcVersionID  ALWAYS the same version — a restore overwrites in place, it never forks
 * @property {number} restoredFromNo @property {number} functionCount
 * @property {string[]} warnings  Thai — e.g. a `fieldKey` that has since been deleted from the rule registry.
 *   Never a reason to render the restore as failed: what came back is what used to be correct.
 *
 * @typedef {Object} CalcBundleSource  provenance of an export — DISPLAY ONLY, it never picks the destination
 * @property {?string} taxYearCode @property {?number} beYear
 * @property {?string} configVersionCode @property {?string} calcVersionCode @property {?string} description
 *
 * @typedef {Object} CalcBundleFunction
 * @property {string} functionCode  natural key AND the C# class name inside `sourceCode` — carried verbatim
 * @property {?string} name @property {?string} description
 * @property {?string} fieldKey  the OUTPUT claim (a soft reference, not a foreign key)
 * @property {string} sourceCode @property {number} sortOrder @property {boolean} isActive
 * @property {?ParameterBinding[]} parameterBindings  the INPUT wiring
 *
 * @typedef {Object} CalcBundle  the export/import file. Cross-machine portable: no server ids, no audit
 *   columns and NO compile cache survive the export — the target machine compiles for itself.
 * @property {string} kind  always `'lhb.taxcalc.v2.bundle'` — sniff this client-side before posting anything
 * @property {string} schemaVersion @property {string} exportedAt @property {?string} exportedBy
 * @property {CalcBundleSource} source @property {CalcBundleFunction[]} functions
 *
 * @typedef {Object} ImportCalcBundleResult
 * @property {?number} calcVersionID  the new version's id — `null` on a dry run (nothing was written)
 * @property {string} versionCode  the code actually used, after normalization + collision handling
 * @property {boolean} versionCodeAdjusted  true when a collision forced `V1` → `V1-2`
 * @property {number} functionCount @property {number} compileErrorCount
 * @property {boolean} dryRun
 * @property {string[]} warnings  Thai. Missing `fieldKey`s and formulas that failed to compile land HERE and
 *   the import still succeeds — show them, a silent partial load is worse than a refusal.
 */

import axios from 'axios';

const BASE_URL = import.meta.env.VITE_APP_API || 'http://localhost:5000/';

// Dedicated instance. `withCredentials` is set explicitly (cookie session auth) so it
// does not depend on the global axios default having been applied first.
const http = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

const API_STATUS_SUCCESS = 0; // APIStatus.Successful

/**
 * Unwrap a ResponseModel `{ message, status, data }` → inner `data`.
 * Throws an Error (with `.status`/`.data`) when the envelope reports failure.
 * Accepts either PascalCase (`Status`/`Data`) or camelCase (`status`/`data`).
 */
function unwrap(response) {
  const body = response && response.data;
  if (body && typeof body === 'object' && ('status' in body || 'Status' in body)) {
    const status = 'status' in body ? body.status : body.Status;
    const data = 'data' in body ? body.data : body.Data;
    if (status === API_STATUS_SUCCESS) {
      return data;
    }
    const err = new Error(body.message || body.Message || 'Request failed');
    err.status = status;
    err.data = data;
    throw err;
  }
  return body;
}

/** Normalize an axios/transport error into a friendly Error carrying the API message. */
function toError(error) {
  const body = error && error.response && error.response.data;
  const message =
    (body && (body.message || body.Message)) ||
    (error && error.message) ||
    'Request failed';
  const err = new Error(message);
  err.httpStatus = error && error.response && error.response.status;
  err.status = body && (body.status ?? body.Status);
  err.data = body && (body.data ?? body.Data);
  return err;
}

/** An Error thrown by `unwrap` is already shaped; anything else came off the wire. */
function rethrow(e) {
  throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
}

async function get(url) {
  try {
    return unwrap(await http.get(url));
  } catch (e) {
    return rethrow(e);
  }
}
async function post(url, payload) {
  try {
    return unwrap(await http.post(url, payload));
  } catch (e) {
    return rethrow(e);
  }
}
async function put(url, payload) {
  try {
    return unwrap(await http.put(url, payload));
  } catch (e) {
    return rethrow(e);
  }
}
async function del(url) {
  try {
    return unwrap(await http.delete(url));
  } catch (e) {
    return rethrow(e);
  }
}

const ROOT = 'api/v1/TaxCalculateV2';

export const taxCalculateV2Api = {
  // ── Calc Versions (scoped to a Tax Config V2 version; single-active) ──────────

  /**
   * Every calc version under one Tax Config V2 version, newest-authored first as the
   * backend orders them.
   * @param {number|string} configVersionId TaxConfigVersionV2.ID
   * @param {boolean} [includeInactive=true] false → only the active one comes back
   * @returns {Promise<CalcVersion[]>}
   */
  listVersions(configVersionId, includeInactive = true) {
    return get(`${ROOT}/config-versions/${configVersionId}/versions?includeInactive=${includeInactive}`);
  },

  /**
   * The one active calc version of a config version. The backend answers 404 when none
   * is active — that rejects here, so callers that treat "no active version" as a normal
   * state must catch it rather than let it surface as a load error.
   * @param {number|string} configVersionId
   * @returns {Promise<CalcVersion>}
   */
  getActiveVersion(configVersionId) {
    return get(`${ROOT}/config-versions/${configVersionId}/versions/active`);
  },

  /**
   * Create a calc version under a config version. `isActive: true` deactivates the
   * siblings; omit it (or send false) for the usual draft.
   * @param {number|string} configVersionId
   * @param {{ versionCode: string, description?: string, isActive?: boolean }} body
   * @returns {Promise<CalcVersion>} the created row
   */
  createVersion(configVersionId, body) {
    return post(`${ROOT}/config-versions/${configVersionId}/versions`, body);
  },

  /**
   * One calc version by id (carries `functionCount`).
   * @param {number|string} calcVersionId
   * @returns {Promise<CalcVersion>}
   */
  getVersion(calcVersionId) {
    return get(`${ROOT}/versions/${calcVersionId}`);
  },

  /**
   * Rename / re-describe a calc version. Full replace of the editable fields — send
   * `versionCode` even when only the description changed.
   * @param {number|string} calcVersionId
   * @param {{ versionCode: string, description?: string, isActive?: boolean }} body
   * @returns {Promise<CalcVersion>} the updated row
   */
  updateVersion(calcVersionId, body) {
    return put(`${ROOT}/versions/${calcVersionId}`, body);
  },

  /**
   * Delete a calc version **and every function under it** (cascade). Show the
   * `functionCount` blast radius in the confirm dialog before calling this.
   * @param {number|string} calcVersionId
   * @returns {Promise<boolean>}
   */
  deleteVersion(calcVersionId) {
    return del(`${ROOT}/versions/${calcVersionId}`);
  },

  /**
   * Activate / deactivate a calc version. Single-active per config version: activating
   * one **deactivates all its siblings server-side**, so reflect that across the whole
   * list (a caller that only flips the clicked row will show two ACTIVE chips).
   * @param {number|string} calcVersionId
   * @param {boolean} isActive
   * @returns {Promise<boolean>}
   */
  setVersionActive(calcVersionId, isActive) {
    return put(`${ROOT}/versions/${calcVersionId}/active`, { isActive: Boolean(isActive) });
  },

  /**
   * Clone a calc version with all of its functions (source code, sort order and compile
   * state included). The clone is ALWAYS created inactive — there is no `isActive` to send.
   * @param {number|string} calcVersionId source version
   * @param {{ versionCode: string, description?: string }} body
   * @returns {Promise<CalcVersion>} the new (inactive) clone
   */
  cloneVersion(calcVersionId, body) {
    return post(`${ROOT}/versions/${calcVersionId}/clone`, body);
  },

  // ── Functions (scoped to a calc version) ─────────────────────────────────────

  /**
   * The function list of a calc version — summaries only (no `sourceCode`), which is
   * why the row shows `compileStatus`/`isStale` instead of the body.
   * @param {number|string} calcVersionId
   * @param {boolean} [includeInactive=true]
   * @returns {Promise<FunctionSummary[]>}
   */
  listFunctions(calcVersionId, includeInactive = true) {
    return get(`${ROOT}/versions/${calcVersionId}/functions?includeInactive=${includeInactive}`);
  },

  /**
   * Create a function. A blank `sourceCode` makes the backend seed a working starter
   * function. Resolves even when the new source does not compile — read `compileStatus`
   * and `compileErrors` off the returned row.
   * @param {number|string} calcVersionId
   * @param {{ functionCode: string, name?: string, description?: string, fieldKey?: string,
   *           sourceCode?: string, sortOrder?: number, isActive?: boolean,
   *           parameterBindings?: ParameterBinding[] }} body
   * @returns {Promise<FunctionFull>} the created row, with its compile outcome
   */
  createFunction(calcVersionId, body) {
    return post(`${ROOT}/versions/${calcVersionId}/functions`, body);
  },

  /**
   * Compile a candidate source WITHOUT saving it — the editor's "ตรวจสอบ" button.
   * Resolves with `ok: false` + `errors[]` on a compile failure (HTTP 200); it does not throw.
   * On success it also reports the discovered `entryPoint`, `returnType` and `parameters`,
   * which is how the run panel learns what inputs to render.
   * @param {number|string} calcVersionId
   * @param {{ functionCode: string, sourceCode: string }} body
   * @returns {Promise<ValidateResult>}
   */
  validateFunction(calcVersionId, body) {
    return post(`${ROOT}/versions/${calcVersionId}/validate`, body);
  },

  /**
   * One function with its `sourceCode`, `sourceHash`, `compileErrors[]` and `parameterBindings[]`.
   * A row saved before parameter binding existed answers with the field absent — read it as "no
   * bindings yet", and do NOT treat an absent field as a reason to wipe what the editor holds.
   * @param {number|string} functionId
   * @returns {Promise<FunctionFull>}
   */
  getFunction(functionId) {
    return get(`${ROOT}/functions/${functionId}`);
  },

  /**
   * Save a function. Same all-or-nothing body as create — send every field, not a patch.
   *
   * 🔴 `functionCode` is the generated class name. Sending a NEW `functionCode` **without**
   * `sourceCode` makes the backend rewrite the class declaration in the stored source for you.
   * Sending both means your source wins verbatim — so if the user edited the code *and* the
   * body, send both and surface whatever the compiler says instead of guessing.
   *
   * Resolves even when the code fails to compile: the outcome lands on the returned row
   * (`compileStatus`, `compileErrorCount`, `compileErrors`), and the save itself still happened.
   *
   * `parameterBindings` is the exception to "send every field": omitting it (or sending `null`)
   * PRESERVES the stored bindings, `[]` clears them, and a non-empty array replaces the whole set.
   * That is what lets a sort-order-only save leave the wiring alone. An editor that shows the table
   * still sends the complete list, orphan entries included.
   * @param {number|string} functionId
   * @param {{ functionCode: string, name?: string, description?: string, fieldKey?: string,
   *           sourceCode?: string, sortOrder?: number, isActive?: boolean,
   *           parameterBindings?: ParameterBinding[] }} body
   * @returns {Promise<FunctionFull>} the saved row, with its compile outcome
   */
  updateFunction(functionId, body) {
    return put(`${ROOT}/functions/${functionId}`, body);
  },

  /**
   * Delete one function from its calc version.
   * @param {number|string} functionId
   * @returns {Promise<boolean>}
   */
  deleteFunction(functionId) {
    return del(`${ROOT}/functions/${functionId}`);
  },

  /**
   * Run a function against supplied arguments. Pass `sourceCode` to run the UNSAVED buffer
   * in the editor (a scratch compile — nothing is persisted); omit it to run the stored source.
   *
   * `arguments` is keyed by parameter name. A parameter with `required: true` (i.e. no
   * default) that is missing comes back as `ok: false`, `errorKind: 'binding'` and
   * `errorParameter: '<name>'` — a normal resolved response, NOT a rejection.
   *
   * Optional `formId` runs against a real tax form: the formula's `parameterBindings` are resolved
   * from that form's answers, and anything in `arguments` still WINS over what they resolved (that
   * is how the panel tries a what-if value against real data). A field that is Skipped /
   * NotApplicable / unanswered is left UNBOUND — never sent as 0 — so the parameter falls back to
   * its source default or the run fails with `errorKind: 'binding'`. `warnings[]` says what the
   * resolver refused to guess and `missingParameters[]` lists every required parameter still empty.
   * @param {number|string} functionId
   * @param {{ sourceCode?: string, arguments: Object<string, *>, formId?: number }} body
   * @returns {Promise<RunResult>}
   */
  runFunction(functionId, body) {
    return post(`${ROOT}/functions/${functionId}/run`, body);
  },

  // ── Save history (snapshots of one calc version) ─────────────────────────────

  /**
   * The save-history of one calc version, newest (`isCurrent: true`) first.
   *
   * The rows come back WITHOUT `payloadJson` (it is null on every one) — one snapshot is the whole
   * formula set, so shipping 50 of them would mean shipping the source code 50 times. Fetch the one
   * you actually need with `getSnapshot`. A version nobody has written yet answers `[]` (200, not 404).
   * @param {number|string} calcVersionId
   * @returns {Promise<CalcSnapshot[]>}
   */
  listHistory(calcVersionId) {
    return get(`${ROOT}/versions/${calcVersionId}/history`);
  },

  /**
   * One snapshot WITH its `payloadJson` — the formula rows as a raw JSON string. It stays a STRING
   * here on purpose (the wire never carries it parsed); parse it in the dialog, or hand both strings
   * straight to `utils/taxCalcSnapshotDiff.js` → `diffSnapshots(left, right)`.
   * @param {number|string} snapshotId
   * @returns {Promise<CalcSnapshot>} 404 → rejected Error
   */
  getSnapshot(snapshotId) {
    return get(`${ROOT}/history/${snapshotId}`);
  },

  /**
   * Restore a snapshot over the CURRENT formulas of its own calc version. The `calcVersionID` never
   * changes and surviving rows are updated in place, so bookmarked `/function/:functionId` routes and
   * CALCULATE items pinned to this version stay valid. The restore is itself recorded as a new
   * snapshot, so it can be rolled back again.
   *
   * Reload the whole function list afterwards — row count, sort order and compile state can all have moved.
   * @param {number|string} snapshotId
   * @param {?{comment?:?string}} [body] optional note (defaults server-side to `Restored from v{N}`)
   * @returns {Promise<RestoreCalcSnapshotResult>} `warnings` may be non-empty on a SUCCESSFUL restore
   */
  restoreSnapshot(snapshotId, body) {
    return post(`${ROOT}/history/${snapshotId}/restore`, body || {});
  },

  // ── Export / Import (move a calc version between machines) ───────────────────

  /**
   * Export one calc version — every function, inactive rows included — as a portable bundle.
   *
   * Returns the bundle OBJECT: the endpoint answers with a normal `ResponseModel`, NOT a file
   * download. The browser is what turns it into a `.json` (Blob + `<a download>`, and defer
   * `URL.revokeObjectURL` with `setTimeout(…, 0)` or Safari cancels the download).
   * @param {number|string} calcVersionId
   * @returns {Promise<CalcBundle>} 404 → rejected Error
   */
  exportVersion(calcVersionId) {
    return get(`${ROOT}/versions/${calcVersionId}/export`);
  },

  /**
   * Import a bundle as a NEW calc version. The new version is ALWAYS created inactive, so an import
   * can never knock the live calc set off the air.
   *
   * Anything the target machine is missing (a `fieldKey` with no rule, a formula that will not compile
   * here) is REPORTED in `warnings` and the import proceeds — it never 400s the whole file. Read
   * `warnings` / `compileErrorCount` and surface them.
   * @param {Object} args
   * @param {CalcBundle} args.bundle  the parsed file. Untrusted input — only its content is used;
   *   `bundle.source.*` is display-only and never resolves the target.
   * @param {number} args.taxConfigVersionID  config version the new calc version is created under. Comes
   *   from the UI (the row the Import button sits under), NEVER from the file. 🔴 Trailing capital `ID`
   *   — that is what ASP.NET's camelCase policy emits; a `…Id` key is received as null, silently.
   *   (A `taxConfigVersionId` spelling is accepted here and forwarded under the right name.)
   * @param {?string} [args.versionCode]  blank ⇒ the file's `source.calcVersionCode`. Either way a
   *   collision auto-increments (`V1` → `V1-2`) and the result reports `versionCodeAdjusted: true`.
   * @param {?string} [args.description]  blank ⇒ the file's `source.description`.
   * @param {boolean} [args.dryRun=false]  true ⇒ run the whole remap and report counts + warnings but write
   *   NOTHING to the database and compile nothing — this is what powers the preview panel;
   *   `calcVersionID` comes back `null`.
   * @returns {Promise<ImportCalcBundleResult>}
   */
  importBundle({ bundle, taxConfigVersionID, taxConfigVersionId, versionCode, description, dryRun } = {}) {
    return post(`${ROOT}/import`, {
      bundle,
      // 🔴 trailing capital `ID`, exactly as ASP.NET serializes it — `taxConfigVersionId` arrives as null.
      taxConfigVersionID: taxConfigVersionID ?? taxConfigVersionId ?? null,
      versionCode: versionCode || null,
      description: description || null,
      dryRun: Boolean(dryRun),
    });
  },
};

export default taxCalculateV2Api;
